<template>
  <section class="isp-user-edit">
    <b-card no-body class="mb-2">
      <b-card-body>
        <div class="d-flex flex-wrap align-items-start justify-content-between">
          <div class="mb-1">
            <h4 class="mb-0">Edit Connection</h4>
            <small class="text-muted">Update PPP, billing, and location details.</small>
          </div>
          <div class="d-flex align-items-center">
            <b-badge v-if="hasStatus" :variant="statusBadgeVariant">
              {{ statusLabel }}
            </b-badge>
          </div>
        </div>

        <div v-if="isLoading" class="edit-skeleton">
          <div class="skeleton-line skeleton-line--lg"></div>
          <div class="skeleton-line"></div>
          <div class="skeleton-grid">
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line"></div>
          </div>
        </div>
        <b-alert v-else-if="loadError" variant="danger" show>
          {{ loadError }}
        </b-alert>
        <div v-else class="user-summary">
          <div class="summary-header">
            <div>
              <h5 class="mb-0">{{ userName }}</h5>
              <small class="text-muted">Pair Code: {{ userPairCode }}</small>
            </div>
            <small class="text-muted">{{ userMobile }}</small>
          </div>
          <div class="summary-grid">
            <div>
              <small class="text-muted">Email</small>
              <div>{{ userEmail }}</div>
            </div>
            <div>
              <small class="text-muted">Address</small>
              <div>{{ userAddress }}</div>
            </div>
          </div>
        </div>
      </b-card-body>
    </b-card>

    <validation-observer v-if="!isLoading && !loadError" ref="simpleRules">
      <b-form>
        <b-card no-body class="section-card mb-2">
          <b-card-body>
            <div class="section-title">Connection Details</div>
            <b-row>
              <input
                v-model="usernameInfo.user_id"
                type="hidden"
                class="form-control"
              >
              <b-col md="6">
                <b-form-group label="Package Name" label-for="package_name">
                  <validation-provider #default="{ errors }" name="Package Name" rules="required">
                    <v-select
                      v-model="usernameInfo.package_id"
                      :options="package_list"
                      :reduce="(val) => val.value"
                      placeholder="Select Package"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :clearable="false"
                      @input="(packageData) => changePackage(package_list, packageData)"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Monthly Bill" label-for="package_price">
                  <validation-provider #default="{ errors }" name="package_price" rules="required">
                    <b-form-input
                      v-model="usernameInfo.package_price"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Monthly Bill"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Router Name" label-for="router_id">
                  <validation-provider #default="{ errors }" name="Router Name" rules="required">
                    <v-select
                      v-model="usernameInfo.router_id"
                      :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                      :options="router_list"
                      :reduce="(val) => val.value"
                      :clearable="false"
                      input-id="user-role"
                      placeholder="Select Router"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="PPP Username" label-for="user_name">
                  <validation-provider #default="{ errors }" name="user_name" rules="required">
                    <b-form-input
                      v-model="usernameInfo.user_name"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Enter PPP Username"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Connection Name" label-for="title">
                  <validation-provider #default="{ errors }" name="title" rules="required">
                    <b-form-input
                      v-model="usernameInfo.title"
                      :state="errors.length > 0 ? false : null"
                      placeholder="Enter Connection Name"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Status" label-for="status">
                  <validation-provider #default="{ errors }" name="status" rules="required">
                    <div class="d-flex align-items-center">
                      <b-form-checkbox
                        v-model="usernameInfo.status_value"
                        switch
                        value="1"
                        unchecked-value="0"
                        :disabled="isTogglingStatus || isLoading"
                        @change="onStatusToggle"
                      >
                        {{ statusLabel }}
                      </b-form-checkbox>
                      <span v-if="isTogglingStatus" class="spinner-border spinner-border-sm ml-1" />
                    </div>
                    <small class="text-muted d-block">
                      Off করলে PPP Secret disable হবে এবং active session disconnect হবে।
                    </small>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="12">
                <b-form-group label="Auto Generate" label-for="autogenerate">
                  <validation-provider #default="{ errors }" name="autogenerate" rules="">
                    <b-form-checkbox v-model="usernameInfo.autogenerate" value="1">
                      Automatic Invoice
                    </b-form-checkbox>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <b-card no-body class="section-card mb-2">
          <b-card-body>
            <div class="section-title">Create New Invoice</div>
            <b-row>
              <b-col md="6">
                <b-form-group label="Invoice Date" label-for="invoice_date">
                  <validation-provider #default="{ errors }" name="invoice_date" rules="">
                    <b-form-datepicker
                      id="invoice_date"
                      v-model="usernameInfo.invoice_date"
                      class="mb-1"
                      :disabled="autogenerateDisable"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Invoice Due Date" label-for="next_due_date">
                  <validation-provider #default="{ errors }" name="next_due_date" rules="">
                    <b-form-datepicker
                      id="next_due_date"
                      v-model="usernameInfo.next_due_date"
                      class="mb-1"
                      :disabled="autogenerateDisable"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col cols="12">
                <h6 v-if="usernameInfo.invoice_statu == 0" class="text-danger">
                  You are not able to make invoice, please contact with admin
                </h6>
                <b-button
                  v-if="usernameInfo.invoice_status"
                  variant="success mr-2"
                  type="submit"
                  :disabled="autogenerateDisable"
                  @click.prevent="createNewInvoice"
                >
                  Make New Invoice
                </b-button>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>

        <b-card no-body class="section-card mb-2">
          <b-card-body>
            <div class="section-title">Location (Required)</div>
            <b-row>
              <b-col md="6">
                <b-form-group label="Latitude" label-for="latitude">
                  <validation-provider #default="{ errors }" name="latitude" rules="">
                    <b-form-input
                      id="latitude"
                      v-model="usernameInfo.latitude"
                      type="number"
                      step="0.000001"
                      min="-90"
                      max="90"
                      placeholder="e.g. 23.7806"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                    <small class="text-muted d-block">Latitude range: -90 to 90</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Longitude" label-for="longitude">
                  <validation-provider #default="{ errors }" name="longitude" rules="">
                    <b-form-input
                      id="longitude"
                      v-model="usernameInfo.longitude"
                      type="number"
                      step="0.000001"
                      min="-180"
                      max="180"
                      placeholder="e.g. 90.4125"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                    <small class="text-muted d-block">Longitude range: -180 to 180</small>
                  </validation-provider>
                </b-form-group>
              </b-col>
            </b-row>
            <small class="text-muted d-block mb-1">Click on the map to set latitude & longitude.</small>
            <div class="location-actions mb-1">
              <b-button
                size="sm"
                variant="outline-primary"
                class="mr-1"
                :disabled="isLocating"
                @click="useMyLocation"
              >
                <span v-if="isLocating" class="spinner-border spinner-border-sm mr-50" />
                Use my location
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                :disabled="!mapMarker"
                @click="centerOnMarker"
              >
                Center marker
              </b-button>
            </div>
            <small v-if="locationMessage" :class="locationMessageClass">{{ locationMessage }}</small>
            <div class="location-map">
              <l-map
                ref="locationMap"
                :zoom="mapZoom"
                :center="mapCenter"
                @ready="onMapReady"
                @update:zoom="mapZoom = $event"
                @update:center="mapCenter = $event"
              >
                <l-tile-layer :url="tileUrl" :attribution="tileAttribution" />
                <l-marker
                  v-if="mapMarker"
                  :lat-lng="mapMarker"
                  :icon="mapMarkerIcon"
                  :draggable="true"
                  @dragend="onMarkerDragEnd"
                />
              </l-map>
            </div>
          </b-card-body>
        </b-card>

        <div class="action-bar">
          <b-button
            variant="primary"
            class="mr-1"
            :disabled="isSaving"
            @click.prevent="validationForm"
          >
            <span v-if="isSaving" class="spinner-border spinner-border-sm mr-50" />
            Save Changes
          </b-button>
          <b-button
            v-ripple.400="'rgba(113, 102, 240, 0.15)'"
            variant="outline-danger"
            :disabled="isSaving"
            @click="confirmDeleteUserNames"
          >
            Delete
          </b-button>
        </div>
      </b-form>
    </validation-observer>
  </section>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { LMap, LTileLayer, LMarker } from 'vue2-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  BFormDatepicker,
  BFormCheckbox,
  BFormInput,
  BFormGroup,
  BForm,
  BRow,
  BCol,
  BButton,
  BFormSelect,
  BCard,
  BCardBody,
  BAlert,
  BBadge,
} from 'bootstrap-vue'
import { required, email } from '@validations'
import useJwt from '@/auth/jwt/useJwt'
import Ripple from 'vue-ripple-directive'
import {
  ISP_PACKAGE_LIST,
  ISP_ROUTER_LIST,
  USERNAME_VIEW,
  USERNAME_TOGGLE_PPP,
} from '@core/services/api'
import vSelect from 'vue-select'
import router from '@/router'
import Mixin from '@core/services/mixin'
import { checkRes } from '@core/services/helper'
import $ from 'jquery'

export default {
  directives: {
    Ripple,
  },
  components: {
    BFormDatepicker,
    BFormCheckbox,
    BFormSelect,
    ValidationProvider,
    ValidationObserver,
    BFormInput,
    BFormGroup,
    BForm,
    BRow,
    BCol,
    BButton,
    vSelect,
    BCard,
    BCardBody,
    BAlert,
    BBadge,
    LMap,
    LTileLayer,
    LMarker,
  },
  mixins: [Mixin],
  data() {
    return {
      usernameId: router.currentRoute.params.usernameId,
      userData: null,
      usernameInfo: {},
      package_list: [],
      user_list: [],
      router_list: [],
      emailValue: '',
      name: '',
      required,
      invoice_date: null,
      next_due_date: null,
      autogenerate: null,
      selected: [1, 0],
      email,
      status_value: null,
      isLoading: true,
      isSaving: false,
      isTogglingStatus: false,
      loadError: null,
      isp_id: null,
      mapCenter: [23.685, 90.3563],
      mapZoom: 13,
      mapMarker: null,
      mapMarkerIcon: null,
      mapInstance: null,
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
      tileAttribution: '&copy; OpenStreetMap contributors',
      isLocating: false,
      locationMessage: '',
      locationMessageType: 'muted',
      locationMessageTimer: null,
      options_status: [
        { value: '1', text: 'Active' },
        { value: '0', text: 'Inactive' },
      ],
      autogenerateDisable: false,
    }
  },
  computed: {
    userName() {
      return (this.userData && this.userData.name) ? this.userData.name : 'User'
    },
    userPairCode() {
      return (this.userData && this.userData.pair_code) ? this.userData.pair_code : '-'
    },
    userMobile() {
      return (this.userData && this.userData.mobile) ? this.userData.mobile : '-'
    },
    userEmail() {
      return (this.userData && this.userData.email) ? this.userData.email : '-'
    },
    userAddress() {
      if (!this.userData) return '-'
      if (this.userData.address) return this.userData.address
      if (this.userData.others && this.userData.others[0] && this.userData.others[0].address) {
        return this.userData.others[0].address
      }
      return '-'
    },
    hasStatus() {
      return this.usernameInfo && this.usernameInfo.status_value !== undefined && this.usernameInfo.status_value !== null
    },
    statusLabel() {
      return this.usernameInfo.status_value == 1 || this.usernameInfo.status_value === '1' ? 'Active' : 'Inactive'
    },
    statusBadgeVariant() {
      return this.usernameInfo.status_value == 1 || this.usernameInfo.status_value === '1' ? 'light-success' : 'light-danger'
    },
    locationMessageClass() {
      return this.locationMessageType === 'success'
        ? 'text-success'
        : this.locationMessageType === 'danger'
          ? 'text-danger'
          : 'text-muted'
    },
  },
  mounted() {
    this.mapMarkerIcon = this.createMarkerIcon()
    this.isp_id = $('#ispListId option:selected').val()
    this.packageListData(this.isp_id)
    this.routerListData(this.isp_id)
    const instance = this
    $('#ispListId').change(function () {
      this.isp_id = $(this).children('option:selected').val()
      instance.packageListData(this.isp_id)
      instance.routerListData(this.isp_id)
    })
    this.getUserNameData()
  },
  watch: {
    'usernameInfo.latitude'() {
      this.syncMapFromInputs()
    },
    'usernameInfo.longitude'() {
      this.syncMapFromInputs()
    },
  },
  methods: {
    changePackage(package_price, packageData) {
      if (package_price) {
        for (const package_price of package_price) {
          if (package_price.value == packageData) {
            this.usernameInfo.package_price = package_price.price
            this.usernameInfo.package_id = package_price.value
          }
        }
      }
    },
    getUserNameData() {
      this.isLoading = true
      this.loadError = null
      this.$http
        .get(process.env.VUE_APP_BASEURL + USERNAME_VIEW + this.usernameId)
        .then(res => {
          const data = res.data.data || {}
          this.usernameInfo = {
            ...data,
            latitude: data.latitude ?? '',
            longitude: data.longitude ?? '',
          }
          if (this.usernameInfo.status_value !== undefined && this.usernameInfo.status_value !== null) {
            this.usernameInfo.status_value = String(this.usernameInfo.status_value)
          }
          this.userData = data.users || null
          this.autogenerateDisable = this.usernameInfo.autogenerate != 0
          this.syncMapFromInputs()
        })
        .catch(error => {
          this.loadError = 'Failed to load connection details.'
          if (error.response && error.response.status === 404) {
            userData.value = undefined
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    onStatusToggle(value) {
      const nextStatus = value === '1' || value === 1
      if (this.isTogglingStatus) return
      this.isTogglingStatus = true
      const previousValue = nextStatus ? '0' : '1'
      const payload = {
        user_id: this.usernameId,
        status: nextStatus ? 1 : 0,
      }

      this.$http
        .post(process.env.VUE_APP_BASEURL + USERNAME_TOGGLE_PPP, payload)
        .then(response => {
          if (checkRes(response.data.code)) {
            this.usernameInfo.status_value = nextStatus ? '1' : '0'
            this.toastMessage('success', 'PPP Status', response)
          } else {
            this.usernameInfo.status_value = previousValue
            this.toastMessage('warning', 'PPP Status', response)
          }
        })
        .catch(error => {
          this.usernameInfo.status_value = previousValue
          console.log(error)
        })
        .finally(() => {
          this.isTogglingStatus = false
        })
    },
    onMapReady(map) {
      this.mapInstance = map
      if (this.mapInstance && this.mapInstance.on) {
        this.mapInstance.on('click', this.onMapClick)
      }
    },
    onMapClick(event) {
      if (!event || !event.latlng) return
      this.setCoordinates(event.latlng.lat, event.latlng.lng)
    },
    onMarkerDragEnd(event) {
      const target = event && event.target ? event.target : null
      if (!target || !target.getLatLng) return
      const { lat, lng } = target.getLatLng()
      this.setCoordinates(lat, lng)
    },
    useMyLocation() {
      if (!navigator.geolocation) {
        this.setLocationMessage('Geolocation is not supported on this device.', 'danger')
        return
      }
      this.isLocating = true
      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords
          this.setCoordinates(latitude, longitude)
          this.setLocationMessage('Location captured from this device.', 'success')
          this.isLocating = false
        },
        error => {
          const message = error && error.code === 1
            ? 'Location permission denied.'
            : error && error.code === 2
              ? 'Location unavailable.'
              : 'Unable to fetch location.'
          this.setLocationMessage(message, 'danger')
          this.isLocating = false
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      )
    },
    centerOnMarker() {
      if (!this.mapMarker) return
      this.mapCenter = [...this.mapMarker]
      if (this.mapZoom < 15) {
        this.mapZoom = 15
      }
    },
    setCoordinates(lat, lng) {
      const parsedLat = this.parseCoordinate(lat)
      const parsedLng = this.parseCoordinate(lng)
      if (parsedLat === null || parsedLng === null) return
      this.usernameInfo.latitude = parsedLat.toFixed(6)
      this.usernameInfo.longitude = parsedLng.toFixed(6)
      this.mapCenter = [parsedLat, parsedLng]
      this.mapMarker = [parsedLat, parsedLng]
    },
    syncMapFromInputs() {
      const parsedLat = this.parseCoordinate(this.usernameInfo.latitude)
      const parsedLng = this.parseCoordinate(this.usernameInfo.longitude)
      if (parsedLat === null || parsedLng === null) {
        this.mapMarker = null
        return
      }
      this.mapCenter = [parsedLat, parsedLng]
      this.mapMarker = [parsedLat, parsedLng]
    },
    parseCoordinate(value) {
      if (value === null || value === undefined || value === '') return null
      const parsed = Number(value)
      if (Number.isNaN(parsed)) return null
      return parsed
    },
    createMarkerIcon() {
      return L.divIcon({
        className: 'map-pin',
        iconSize: [26, 36],
        iconAnchor: [13, 36],
        popupAnchor: [0, -30],
        html: `
          <svg class="map-pin__svg" viewBox="0 0 24 36" aria-hidden="true">
            <path d="M12 0C5.372 0 0 5.372 0 12c0 9.2 12 24 12 24s12-14.8 12-24C24 5.372 18.628 0 12 0z" fill="#ea4335"/>
            <circle cx="12" cy="12" r="5" fill="#ffffff"/>
          </svg>
        `,
      })
    },
    setLocationMessage(message, type) {
      this.locationMessage = message
      this.locationMessageType = type || 'muted'
      window.clearTimeout(this.locationMessageTimer)
      this.locationMessageTimer = window.setTimeout(() => {
        this.locationMessage = ''
      }, 4000)
    },
    packageListData(ispId) {
      this.$http
        .get(
          `${process.env.VUE_APP_BASEURL + ISP_PACKAGE_LIST}?isp_id=${ispId}`,
        )
        .then(res => {
          this.package_list = res.data.data
        })
        .catch(error => {
          if (error.response.status === 404) {
            userData.value = undefined
          }
        })
    },
    routerListData(ispId) {
      this.$http
        .get(`${process.env.VUE_APP_BASEURL + ISP_ROUTER_LIST}?isp_id=${ispId}`)
        .then(res => {
          this.router_list = res.data.data
        })
        .catch(error => {
          if (error.response.status === 404) {
            userData.value = undefined
          }
        })
    },
    validationForm() {
      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          this.isSaving = true
          useJwt
            .updateCustomerUserName(this.usernameId, {
              user_id: this.usernameInfo.user_id,
              router_id: this.usernameInfo.router_id,
              package_id: this.usernameInfo.package_id,
              package_price: this.usernameInfo.package_price,
              title: this.usernameInfo.title,
              user_name: this.usernameInfo.user_name,
              autogenerate: this.usernameInfo.autogenerate,
              status: this.usernameInfo.status_value,
              latitude: this.usernameInfo.latitude,
              longitude: this.usernameInfo.longitude,
              do_not_update_meta: true,
            })
            .then(response => {
              if (checkRes(response.data.code)) {
                this.autogenerateDisable = response.data.data.autogenerate != 0
                this.$router
                  .push({ name: 'isp-user-name-edit' })
                  .catch(error => {
                    this.toastMessage('success', 'Assign Dail Up', response)
                  })
              } else {
                this.$router
                  .push({ name: 'isp-user-name-edit' })
                  .catch(error => {
                    this.toastMessage('warning', 'Assign Dail Up', response)
                  })
              }
            })
            .catch(error => {
              console.log(error)
            })
            .finally(() => {
              this.isSaving = false
            })
        }
      })
    },
    // Make New Invoice
    createNewInvoice() {
      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          useJwt
            .createNewInvoice(this.usernameId, {
              isp_id: this.isp_id ?? null,
              user_id: this.usernameInfo.user_id,
              grand_total: this.usernameInfo.package_price,
              next_invoice_date: this.usernameInfo.invoice_date,
              next_due_date: this.usernameInfo.next_due_date,
            })
            .then(response => {
              console.log(response)
              if (checkRes(response.data.code)) {
                this.$router
                  .push({ name: 'isp-user-name-edit' })
                  .catch(error => {
                    this.toastMessage(
                      'success',
                      'Invoice Create Successfully',
                      response,
                    )
                  })
              } else {
                this.$router
                  .push({ name: 'isp-user-name-edit' })
                  .catch(error => {
                    this.toastMessage('warning', 'Invoice', response)
                  })
              }
            })
            .catch(error => {
              console.log(error)
            })
        }
      })
    },
    // comfirm button color
    confirmDeleteUserNames() {
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          useJwt
            .deleteCustomerUserName({
              user_id: this.usernameId,
            })
            .then(response => {
              if (checkRes(response.data.code)) {
                this.$swal({
                  icon: 'success',
                  title: 'Deleted!',
                  text: 'Your file has been deleted.',
                  customClass: {
                    confirmButton: 'btn btn-success',
                  },
                })
                this.$router
                  .push({ name: 'isp-username-list' })
                  .catch(error => {
                    this.toastMessage(
                      'success',
                      'Delete Successfully',
                      response,
                    )
                  })
              } else {
                this.$swal({
                  title: 'Cancelled',
                  text: 'Your imaginary file is safe :)',
                  icon: 'error',
                  customClass: {
                    confirmButton: 'btn btn-success',
                  },
                })
              }
            })
            .catch(error => {
              this.$swal({
                title: 'Cancelled',
                text: 'Your imaginary file is safe :)',
                icon: 'error',
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
            })
        } else if (result.dismiss === 'cancel') {
          this.$swal({
            title: 'Cancelled',
            text: 'Your imaginary file is safe :)',
            icon: 'error',
            customClass: {
              confirmButton: 'btn btn-success',
            },
          })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.isp-user-edit {
  .user-summary {
    background: #f8fafc;
    border: 1px solid #e6e8eb;
    border-radius: 0.75rem;
    padding: 1rem;
    margin-top: 1rem;
  }

  .summary-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0.75rem;
    margin-top: 0.75rem;
  }

  .section-card {
    border: 1px solid #e6e8eb;
    border-radius: 0.75rem;
  }

  .section-title {
    font-weight: 600;
    margin-bottom: 1rem;
  }

  .location-map {
    height: 260px;
    border: 1px solid #e6e8eb;
    border-radius: 0.75rem;
    overflow: hidden;
  }

  .location-map ::v-deep .vue2leaflet-map {
    height: 100%;
  }

  .location-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: center;
  }

  .action-bar {
    position: sticky;
    bottom: 1rem;
    background: #ffffff;
    padding: 0.75rem;
    border: 1px solid #e6e8eb;
    border-radius: 0.75rem;
    display: flex;
    justify-content: flex-start;
    gap: 0.75rem;
    z-index: 10;
    box-shadow: 0 10px 24px rgba(34, 41, 47, 0.08);
  }

  .edit-skeleton {
    margin-top: 1rem;
  }

  .skeleton-line {
    height: 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, #eceff3 0%, #f7f8fa 50%, #eceff3 100%);
    background-size: 200% 100%;
    animation: skeleton-move 1.4s ease-in-out infinite;
    margin-bottom: 0.75rem;
  }

  .skeleton-line--lg {
    height: 18px;
    width: 60%;
  }

  .skeleton-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.75rem;
  }
}

::v-deep .map-pin {
  width: 26px;
  height: 36px;
}

::v-deep .map-pin__svg {
  width: 26px;
  height: 36px;
  filter: drop-shadow(0 6px 10px rgba(34, 41, 47, 0.2));
}

@keyframes skeleton-move {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
